import styled from "styled-components";
import { mobileMaxWidth, colors, fonts, radii } from "../theme";
import AnimatedSection from "./AnimatedSection";
import FixaKorkortImage from "./../assets/images/fixa-korkort-app.png";
import BadtempenImage from "./../assets/images/badtempen-app.png";
import ThermometerImage from "./../assets/images/thermometer-app.png";
import SolaSakertImage from "./../assets/images/sola-sakert-app.png";

const Section = styled.section`
  position: relative;
  overflow: hidden;
  background: ${colors.ink};
  padding: 168px 24px 0;

  /* Soft colour glows */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
        600px circle at 15% 20%,
        rgba(33, 158, 188, 0.28),
        transparent 60%
      ),
      radial-gradient(
        700px circle at 85% 60%,
        rgba(59, 108, 246, 0.22),
        transparent 60%
      );
    pointer-events: none;
  }

  /* Faint grid that fades out towards the bottom */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background-image: linear-gradient(
        rgba(255, 255, 255, 0.04) 1px,
        transparent 1px
      ),
      linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
    background-size: 56px 56px;
    mask-image: radial-gradient(ellipse at 50% 0%, black 30%, transparent 75%);
    -webkit-mask-image: radial-gradient(
      ellipse at 50% 0%,
      black 30%,
      transparent 75%
    );
    pointer-events: none;
  }

  @media screen and (max-width: ${mobileMaxWidth}) {
    padding-top: 128px;
  }
`;

const Inner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1160px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px 6px 8px;
  border-radius: ${radii.round};
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.05);
  color: ${colors.textOnDarkMuted};
  font-family: ${fonts.body};
  font-size: 14px;
  font-weight: 500;
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  margin-left: 6px;
  border-radius: 50%;
  background: #34d399;
  box-shadow: 0 0 0 4px rgba(52, 211, 153, 0.2);
`;

const Title = styled.h1`
  margin: 28px 0 0;
  max-width: 880px;
  color: ${colors.white};
  font-family: ${fonts.heading};
  font-size: clamp(40px, 7vw, 76px);
  font-weight: 800;
  line-height: 1.04;
  letter-spacing: -0.035em;
`;

const Highlight = styled.span`
  background: linear-gradient(90deg, #8ecae6 0%, #219ebc 45%, #6d8bff 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
`;

const Body = styled.p`
  margin: 24px 0 0;
  max-width: 600px;
  color: ${colors.textOnDarkMuted};
  font-family: ${fonts.body};
  font-size: clamp(17px, 2vw, 19px);
  line-height: 1.65;
`;

const Stats = styled.dl`
  margin: 56px 0 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px 56px;
`;

const Stat = styled.div`
  dt {
    order: 2;
    color: ${colors.textOnDarkMuted};
    font-family: ${fonts.body};
    font-size: 14px;
  }

  dd {
    margin: 0;
    color: ${colors.white};
    font-family: ${fonts.heading};
    font-size: 30px;
    font-weight: 700;
    letter-spacing: -0.02em;
  }

  display: flex;
  flex-direction: column-reverse;
  gap: 2px;
`;

const Phones = styled.div`
  --spread: 1;
  position: relative;
  z-index: 1;
  margin: 72px auto 0;
  width: 100%;
  max-width: 1000px;
  height: 460px;

  @media screen and (max-width: ${mobileMaxWidth}) {
    --spread: 0.5;
    height: 280px;
    margin-top: 56px;
  }
`;

// Horizontal offset (px from centre), tilt and size for each slot, outermost first.
const slots = {
  outerLeft: { x: -225, rotate: -12, scale: 0.84, z: 1 },
  innerLeft: { x: -70, rotate: -4, scale: 0.97, z: 3 },
  innerRight: { x: 80, rotate: 5, scale: 0.92, z: 2 },
  outerRight: { x: 230, rotate: 12, scale: 0.84, z: 1 },
};

const Phone = styled.img<{ slot: keyof typeof slots }>`
  position: absolute;
  bottom: -120px;
  left: 50%;
  width: 240px;
  height: auto;
  filter: drop-shadow(0 30px 60px rgba(0, 0, 0, 0.5));
  transform: ${({ slot }) =>
    `translateX(calc(-50% + var(--spread) * ${slots[slot].x}px)) rotate(${slots[slot].rotate}deg) scale(${slots[slot].scale})`};
  transform-origin: bottom center;
  z-index: ${({ slot }) => slots[slot].z};
  opacity: ${({ slot }) => (slots[slot].z === 1 ? 0.85 : 1)};

  @media screen and (max-width: ${mobileMaxWidth}) {
    width: 140px;
    bottom: -70px;
  }
`;

const IntroElement = () => {
  return (
    <Section id="home">
      <Inner>
        <AnimatedSection>
          <Eyebrow>
            <Dot />
            Cross-platform apputveckling
          </Eyebrow>
        </AnimatedSection>
        <AnimatedSection delay={0.08}>
          <Title>
            Vi bygger appar för <Highlight>iOS och Android.</Highlight>
          </Title>
        </AnimatedSection>
        <AnimatedSection delay={0.16}>
          <Body>
            Egna appar, byggda från grunden och använda av tusentals människor
            varje dag.
          </Body>
        </AnimatedSection>
        <AnimatedSection delay={0.32}>
          <Stats>
            <Stat>
              <dt>Publicerade appar</dt>
              <dd>4</dd>
            </Stat>
            <Stat>
              <dt>Nedladdningar</dt>
              <dd>100k+</dd>
            </Stat>
            <Stat>
              <dt>Plattformar</dt>
              <dd>iOS &amp; Android</dd>
            </Stat>
          </Stats>
        </AnimatedSection>
      </Inner>
      <AnimatedSection delay={0.4}>
        <Phones>
          <Phone slot="outerLeft" src={SolaSakertImage} alt="" />
          <Phone
            slot="innerLeft"
            src={FixaKorkortImage}
            alt="Skärmbild från appen Fixa Körkort"
          />
          <Phone
            slot="innerRight"
            src={BadtempenImage}
            alt="Skärmbild från appen Badtempen"
          />
          <Phone slot="outerRight" src={ThermometerImage} alt="" />
        </Phones>
      </AnimatedSection>
    </Section>
  );
};

export default IntroElement;
